<script setup lang="ts">
    import { inject } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { RenewalExclusionReason, SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import Loading from '@shared/components/Loading.vue';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    function getStatusClass(conditionMet: boolean): string {
        return conditionMet ? 'status-not-ok' : 'status-ok';
    }
</script>

<template>
    <Loading
        :show="subscriptionAccess.state.loadingMonthlyPriceBreakdown || subscriptionAccess.state.loadingRenewalExclusionReasons"
        loadingStyle="progress-bar"></Loading>
    <div v-if="!subscriptionAccess.state.loadingMonthlyPriceBreakdown && !subscriptionAccess.state.loadingRenewalExclusionReasons">
        <div class="m-lg-b">
            <b>Monthly price breakdown</b>
            <template
                v-if="
                    subscriptionAccess.state.monthlyPriceBreakdown &&
                    subscriptionAccess.state.boServiceSubscription &&
                    subscriptionAccess.state.monthlyPriceWithAddons
                ">
                <div>
                    Base price:
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceBreakdown.BasePrice,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Extra accounts price ({{
                        formatMoney(
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.PerAccountPrice,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}):
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceBreakdown.ExtraAccountsPrice,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Monthly discount ({{ subscriptionAccess.state.boServiceSubscription.MonthlyDiscount }}%):
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceBreakdown.MonthlyDiscount,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Account discount ({{ subscriptionAccess.state.boServiceSubscription.PerAccountDiscount }}%):
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceBreakdown.AccountDiscount,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Percentual discount ({{ subscriptionAccess.state.boServiceSubscription.MonthlyDiscountPercentage }}%):
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceBreakdown.PercentualDiscount,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Total addons price:
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceWithAddons - subscriptionAccess.state.monthlyPriceBreakdown.TotalPrice,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Total price (excl. addons):
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceBreakdown.TotalPrice,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>
                <div>
                    Total price (incl. addons):
                    {{
                        formatMoney(
                            subscriptionAccess.state.monthlyPriceWithAddons,
                            subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                        )
                    }}
                </div>

                <div class="alert alert-info alert-fixed-i m-lg-t">
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                    The prices above are for right now. The price for the subscription might be different next month.
                </div>
            </template>
        </div>

        <div class="m-lg-b">
            <b>Renewal exclusion reasons</b>
            <template v-if="subscriptionAccess.state.renewalExclusionReason">
                <div>
                    <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.SubscriptionEnded)" class="fa fa-square" aria-hidden="true"></i>
                    {{ subscriptionAccess.state.renewalExclusionReason.SubscriptionEnded ? 'Subscription has ended' : 'Subscription has not ended' }}
                </div>
                <div>
                    <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.BillingStartNotSet)" class="fa fa-square" aria-hidden="true"></i>
                    {{ subscriptionAccess.state.renewalExclusionReason.BillingStartNotSet ? 'Billing start not set' : 'Billing start is set' }}
                </div>
                <div>
                    <i
                        :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.BillingStartInFuture)"
                        class="fa fa-square"
                        aria-hidden="true"></i>
                    {{
                        subscriptionAccess.state.renewalExclusionReason.BillingStartInFuture
                            ? 'Billing starts in the future'
                            : 'Billing does not start in the future'
                    }}
                </div>
                <div>
                    <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.BillingEnded)" class="fa fa-square" aria-hidden="true"></i>
                    {{ subscriptionAccess.state.renewalExclusionReason.BillingEnded ? 'Billing has ended' : 'Billing has not ended' }}
                </div>
                <div>
                    <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.AlreadyPaid)" class="fa fa-square" aria-hidden="true"></i>
                    {{ subscriptionAccess.state.renewalExclusionReason.AlreadyPaid ? 'Already paid' : 'Not already paid' }}
                </div>
                <div>
                    <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.AlreadyRenewed)" class="fa fa-square" aria-hidden="true"></i>
                    {{ subscriptionAccess.state.renewalExclusionReason.AlreadyRenewed ? 'Already renewed' : 'Not already renewed' }}
                </div>
                <div>
                    <i
                        :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.CreditCardPaymentMethod)"
                        class="fa fa-square"
                        aria-hidden="true"></i>
                    {{
                        subscriptionAccess.state.renewalExclusionReason.CreditCardPaymentMethod
                            ? 'Credit card payment method'
                            : 'Not credit card payment method'
                    }}
                </div>
            </template>

            <div class="alert alert-info alert-fixed-i m-lg-t">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                If one of the above is red, the subscription will not show up for renewal this month in billing.
            </div>
        </div>
    </div>
</template>

<style scoped></style>
